import { concluirCriacaoSeCpf } from '@/lib/pedidoAceiteCpf';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolverDestinoRetomada } from '@/lib/retomadaDestino';
import { useToast } from '@/hooks/use-toast';
import { useInvalidateAllPedidoQueries } from '@/hooks/useInvalidateAllPedidoQueries';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { loadBKOTemplates } from '@/lib/mensagemTemplate';
import { rotuloTurno, turnoDaAgenda } from '@/lib/turnoInstalacao';
import { exigirInputFixaAprovado, itensTemFixa } from '@/lib/inputFixaConferencia';
import { marcarTelasDeForaDesatualizadas, meuUsuarioId } from '@/components/kanban/triagem/acaoTriagem';

// Mapeamento de nomes técnicos para nomes amigáveis (fallback para mensagens WhatsApp)
const NOMES_AMIGAVEIS: Record<string, string> = {
  'HA': 'Adicional de Dados',
  'TT MÓVEL': 'Transferência de Titularidade',
  'TT MOVEL': 'Transferência de Titularidade',
  'PN PF-PJ': 'Portabilidade',
  'PN PJ-PJ': 'Portabilidade',
  'MP MOVEL': 'Renovação',
  'MP MÓVEL': 'Renovação',
  'FIBRA': 'Internet Fibra',
  'TT FIXA': 'Transferência de Titularidade Fibra',
  'TV': 'TV por Assinatura',
};

// Função auxiliar para obter nome amigável do produto
function obterNomeAmigavel(produtoNome: string | undefined | null, planoNome: string | undefined | null): string {
  // Priorizar plano_nome se existir (já contém descrição amigável)
  if (planoNome && planoNome.trim()) {
    return planoNome;
  }
  // Fallback para mapeamento de nomes amigáveis
  if (produtoNome) {
    return NOMES_AMIGAVEIS[produtoNome] || produtoNome;
  }
  return 'Produto';
}

// Tipo de quadro compatível com usePedidoMovement
type QuadroEnum = "COMERCIAL" | "CONTRATO_PRIME" | "TRAMITACAO_VIVO" | "QUALIDADE";

// Configuração de ações por coluna
export interface AcaoConfig {
  label: string;
  icone: string;
  proximaColuna: string;
  proximoQuadro?: string; // Quadro de destino (se diferente do atual)
  rpcAtribuicao?: 'aceite' | 'input' | 'tramitacao';
  corClasse: string;
  descricao: string;
  webhookKey?: string; // Chave para configuração de webhook
}

// Configuração de mensagens de webhook por ação com rotação (3 variações)
const MENSAGENS_WEBHOOK: Record<string, { mensagens: string[]; mensagem_privada: string }> = {
  // ===== PRODUTOS MÓVEIS/PN/PP (com assinatura e biometria) =====
  'CRIACAO_PEDIDO': {
    mensagens: [
      `*CLARO EMPRESAS*

Pedido de {{razao_social}} criado com sucesso.
📦 {{produto_nome}} - {{plano_nome}}
📩 Verifique seu e-mail para assinar o contrato e fazer a biometria.`,
      `*CLARO EMPRESAS*

Olá, {{gestor_nome}}!
Seu pedido já foi criado.
📦 {{produto_nome}} - {{plano_nome}}
Agora é só assinar o contrato e fazer a biometria no e-mail.`,
      `*CLARO EMPRESAS*

Pedido criado com sucesso!
📦 {{produto_nome}} - {{plano_nome}}
📌 Próximo passo: assinatura digital + biometria facial no seu e-mail.`
    ],
    mensagem_privada: 'Pedido Criado'
  },
  'CRIACAO_PEDIDO_CONSOLIDADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Pedido de {{razao_social}} criado com sucesso.
📩 Verifique seu e-mail para assinar o contrato e fazer a biometria.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Olá, {{gestor_nome}}!
Seu pedido já foi criado.
Agora é só assinar o contrato e fazer a biometria no e-mail.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Pedido criado com sucesso!
📌 Próximo passo: assinatura digital + biometria facial no seu e-mail.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`
    ],
    mensagem_privada: 'Pedido Criado (Consolidado)'
  },
  'ASSINATURA_REALIZADA': {
    mensagens: [
      `*CLARO EMPRESAS*

Assinatura recebida com sucesso!
📦 {{produto_nome}} - {{plano_nome}}
Seu pedido segue em validação.`,
      `*CLARO EMPRESAS*

Olá, {{gestor_nome}}!
Recebemos sua assinatura digital.
📦 {{produto_nome}} - {{plano_nome}}
Agora seu pedido está em análise pela operadora.`,
      `*CLARO EMPRESAS*

Perfeito, {{gestor_nome}}!
Sua assinatura foi concluída.
📦 {{produto_nome}} - {{plano_nome}}
Já estamos avançando com seu pedido.`
    ],
    mensagem_privada: 'Assinatura Realizada'
  },
  'ASSINATURA_REALIZADA_CONSOLIDADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Assinatura recebida com sucesso!
Seu pedido segue em validação.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Olá, {{gestor_nome}}!
Recebemos sua assinatura digital.
Agora seu pedido está em análise pela operadora.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Perfeito, {{gestor_nome}}!
Sua assinatura foi concluída.
Já estamos avançando com seu pedido.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`
    ],
    mensagem_privada: 'Assinatura Realizada (Consolidado)'
  },
  'PEDIDO_INPUTADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Seu pedido foi enviado para ativação.
📦 {{produto_nome}} - {{plano_nome}}`,
      `*CLARO EMPRESAS*

Pedido inputado no sistema da operadora.
📦 {{produto_nome}} - {{plano_nome}}
Agora é aguardar a ativação.`,
      `*CLARO EMPRESAS*

Tudo certo, {{gestor_nome}}!
📦 {{produto_nome}} - {{plano_nome}}
Seu pedido já está com a operadora para ativação.`
    ],
    mensagem_privada: 'Pedido Inputado'
  },
  'PEDIDO_INPUTADO_CONSOLIDADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Seu pedido foi enviado para ativação.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Pedido inputado no sistema da operadora.
Agora é aguardar a ativação.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Tudo certo, {{gestor_nome}}!
Seu pedido já está com a operadora para ativação.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`
    ],
    mensagem_privada: 'Pedido Inputado (Consolidado)'
  },
  'PEDIDO_ATIVADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Seu pedido foi ativado com sucesso!
📦 {{produto_nome}} - {{plano_nome}}
Já pode usar seus serviços.`,
      `*CLARO EMPRESAS*

Parabéns!
📦 {{produto_nome}} - {{plano_nome}}
Suas linhas já estão ativas e funcionando.`,
      `*CLARO EMPRESAS*

🎉 {{gestor_nome}}, deu tudo certo!
📦 {{produto_nome}} - {{plano_nome}}
Seu pedido foi ativado e seus serviços já estão liberados.
Obrigado pela confiança!`
    ],
    mensagem_privada: 'Pedido Ativado'
  },
  'PEDIDO_ATIVADO_CONSOLIDADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Seu pedido foi ativado com sucesso!
Já pode usar seus serviços.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Parabéns!
Suas linhas já estão ativas e funcionando.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

🎉 {{gestor_nome}}, deu tudo certo!
Seu pedido foi ativado e seus serviços já estão liberados.
Obrigado pela confiança!

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`
    ],
    mensagem_privada: 'Pedido Ativado (Consolidado)'
  },

  // ===== PRODUTOS FIBRA (sem assinatura/biometria, com data de agendamento) =====
  'CRIACAO_PEDIDO_FIBRA': {
    mensagens: [
      `*CLARO EMPRESAS*

Pedido de {{razao_social}} criado com sucesso!
📦 {{produto_nome}} - {{plano_nome}}
📅 Aguarde confirmação do agendamento da instalação.`,
      `*CLARO EMPRESAS*

Olá, {{gestor_nome}}!
Seu pedido de internet fibra foi criado.
📦 {{produto_nome}} - {{plano_nome}}
Em breve entraremos em contato para agendar a instalação.`,
      `*CLARO EMPRESAS*

Pedido criado com sucesso!
📦 {{produto_nome}} - {{plano_nome}}
📌 Próximo passo: agendamento da instalação.`
    ],
    mensagem_privada: 'Pedido Criado (Fibra)'
  },
  'CRIACAO_PEDIDO_FIBRA_CONSOLIDADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Pedido de {{razao_social}} criado com sucesso!
📅 Aguarde confirmação do agendamento da instalação.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Olá, {{gestor_nome}}!
Seu pedido de internet fibra foi criado.
Em breve entraremos em contato para agendar a instalação.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Pedido criado com sucesso!
📌 Próximo passo: agendamento da instalação.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`
    ],
    mensagem_privada: 'Pedido Criado (Fibra - Consolidado)'
  },
  'ASSINATURA_REALIZADA_FIBRA': {
    mensagens: [
      `*CLARO EMPRESAS*

Seu agendamento foi confirmado!
📦 {{produto_nome}} - {{plano_nome}}
🏠 Instalação agendada para: {{data_agendamento}}
Nosso técnico entrará em contato no dia.`,
      `*CLARO EMPRESAS*

Olá, {{gestor_nome}}!
Instalação agendada com sucesso.
📦 {{produto_nome}} - {{plano_nome}}
📅 Data: {{data_agendamento}}
Aguarde contato do técnico.`,
      `*CLARO EMPRESAS*

Tudo certo, {{gestor_nome}}!
📦 {{produto_nome}} - {{plano_nome}}
🏠 Instalação: {{data_agendamento}}
Nosso técnico chegará no horário combinado.`
    ],
    mensagem_privada: 'Agendamento Confirmado (Fibra)'
  },
  'ASSINATURA_REALIZADA_FIBRA_CONSOLIDADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Seu agendamento foi confirmado!
🏠 Instalação agendada para: {{data_agendamento}}
Nosso técnico entrará em contato no dia.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Olá, {{gestor_nome}}!
Instalação agendada com sucesso.
📅 Data: {{data_agendamento}}
Aguarde contato do técnico.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Tudo certo, {{gestor_nome}}!
🏠 Instalação: {{data_agendamento}}
Nosso técnico chegará no horário combinado.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`
    ],
    mensagem_privada: 'Agendamento Confirmado (Fibra - Consolidado)'
  },
  'PEDIDO_INPUTADO_FIBRA': {
    mensagens: [
      `*CLARO EMPRESAS*

Seu pedido foi enviado para a operadora!
📦 {{produto_nome}} - {{plano_nome}}
🏠 Instalação agendada para: {{data_agendamento}}
Aguarde contato do técnico.`,
      `*CLARO EMPRESAS*

Pedido inputado com sucesso.
📦 {{produto_nome}} - {{plano_nome}}
📅 Data de instalação: {{data_agendamento}}
O técnico entrará em contato.`,
      `*CLARO EMPRESAS*

Tudo certo, {{gestor_nome}}!
📦 {{produto_nome}} - {{plano_nome}}
🏠 Instalação: {{data_agendamento}}
Seu pedido já está com a operadora.`
    ],
    mensagem_privada: 'Pedido Inputado (Fibra)'
  },
  'PEDIDO_INPUTADO_FIBRA_CONSOLIDADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Seu pedido foi enviado para a operadora!
🏠 Instalação agendada para: {{data_agendamento}}
Aguarde contato do técnico.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Pedido inputado com sucesso.
📅 Data de instalação: {{data_agendamento}}
O técnico entrará em contato.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Tudo certo, {{gestor_nome}}!
🏠 Instalação: {{data_agendamento}}
Seu pedido já está com a operadora.

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`
    ],
    mensagem_privada: 'Pedido Inputado (Fibra - Consolidado)'
  },
  'PEDIDO_ATIVADO_FIBRA': {
    mensagens: [
      `*CLARO EMPRESAS*

Sua internet fibra foi ativada com sucesso!
📦 {{produto_nome}} - {{plano_nome}}
🎉 Já pode aproveitar sua conexão!`,
      `*CLARO EMPRESAS*

Parabéns, {{gestor_nome}}!
📦 {{produto_nome}} - {{plano_nome}}
Sua fibra está funcionando. Boa navegação!`,
      `*CLARO EMPRESAS*

🎉 Instalação concluída!
📦 {{produto_nome}} - {{plano_nome}}
Sua internet fibra já está ativa.
Obrigado pela confiança!`
    ],
    mensagem_privada: 'Pedido Ativado (Fibra)'
  },
  'PEDIDO_ATIVADO_FIBRA_CONSOLIDADO': {
    mensagens: [
      `*CLARO EMPRESAS*

Sua internet fibra foi ativada com sucesso!
🎉 Já pode aproveitar sua conexão!

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

Parabéns, {{gestor_nome}}!
Sua fibra está funcionando. Boa navegação!

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`,
      `*CLARO EMPRESAS*

🎉 Instalação concluída!
Sua internet fibra já está ativa.
Obrigado pela confiança!

📦 Itens: {{lista_itens}}
💰 Total: R$ {{total_geral}}`
    ],
    mensagem_privada: 'Pedido Ativado (Fibra - Consolidado)'
  }
};

// Função para obter mensagem rotacionada (ciclo 0 → 1 → 2 → 0...)
// Carrega templates do banco automaticamente (com cache interno)
async function obterMensagemRotacionada(webhookKey: string): Promise<string> {
  // Tentar carregar do banco primeiro
  const dbTemplates = await loadBKOTemplates();
  const dbConfig = dbTemplates?.[webhookKey];
  const hardcodedConfig = MENSAGENS_WEBHOOK[webhookKey];
  const config = dbConfig && dbConfig.mensagens.length > 0 ? dbConfig : hardcodedConfig;
  if (!config?.mensagens?.length) return '';
  
  const storageKey = `msg_rotation_${webhookKey}`;
  const ultimaUsada = parseInt(localStorage.getItem(storageKey) || '-1');
  const proxima = (ultimaUsada + 1) % config.mensagens.length;
  localStorage.setItem(storageKey, proxima.toString());
  
  console.log(`🔄 [Rotação] ${webhookKey}: usando variação ${proxima + 1} de ${config.mensagens.length}${dbConfig ? ' (do banco)' : ' (hardcoded)'}`);
  
  return config.mensagens[proxima];
}

// Mapeamento de ações por status/coluna atual
export const ACOES_CONFIG: Record<string, AcaoConfig> = {
  'PEDIDO RECUPERADO': {
    label: 'Retomar Pedido',
    icone: '🔄',
    proximaColuna: 'CRIAÇÃO PEDIDO',
    corClasse: 'bg-warning hover:bg-warning',
    descricao: 'Retomar processamento do pedido recuperado'
  },
  'PEDIDO FECHADO': {
    label: 'Iniciar Criação',
    icone: '🚀',
    proximaColuna: 'CRIAÇÃO PEDIDO',
    corClasse: 'bg-info hover:bg-info',
    descricao: 'Iniciar o processo de criação do pedido'
  },
  'CRIAÇÃO PEDIDO': {
    label: 'Pedido Criado',
    icone: '✅',
    proximaColuna: 'BIOMETRIA',
    corClasse: 'bg-success hover:bg-success',
    descricao: 'Marcar pedido como criado e enviar para biometria',
    webhookKey: 'CRIACAO_PEDIDO'
  },
  'BIOMETRIA': {
    label: 'Biometria Concluída',
    icone: '🪪',
    proximaColuna: 'ACEITE',
    corClasse: 'bg-teal-600 hover:bg-teal-700',
    descricao: 'Biometria concluída — enviar para assinatura (Aceite)'
  },
  'ACEITE': {
    label: 'Assinatura Realizada',
    icone: '📝',
    proximaColuna: 'INPUT',
    rpcAtribuicao: 'input',
    corClasse: 'bg-purple-600 hover:bg-purple-700',
    descricao: 'Confirmar assinatura e enviar para input',
    webhookKey: 'ASSINATURA_REALIZADA'
  },
  'INPUT': {
    label: 'Pedido Inputado',
    icone: '📤',
    proximaColuna: 'INPUT REALIZADO MOVEL', // Default - será sobrescrito dinamicamente por família
    proximoQuadro: 'TRAMITACAO_VIVO',
    rpcAtribuicao: 'tramitacao',
    corClasse: 'bg-indigo-600 hover:bg-indigo-700',
    descricao: 'Confirmar input e aguardar ativação',
    webhookKey: 'PEDIDO_INPUTADO'
  },
  'INPUT REALIZADO MOVEL': {
    label: 'Pedido Ativado',
    icone: '✅',
    proximaColuna: 'FINALIZADO PEDIDO (100%)',
    proximoQuadro: 'QUALIDADE',
    corClasse: 'bg-success hover:bg-success',
    descricao: 'Confirmar ativação e finalizar pedido',
    webhookKey: 'PEDIDO_ATIVADO'
  },
};

interface ExecutarAcaoParams {
  itemId: string;
  pedidoId: string;
  colunaAtual: string;
  colunaOriginal?: string; // Para PEDIDO RECUPERADO - coluna de onde foi cancelado
}

// Params para ação consolidada (múltiplos itens)
interface ExecutarAcaoConsolidadaParams {
  itensIds: string[];
  pedidoId: string;
  colunaAtual: string;
  origemTriagem?: boolean;
  /** Início do turno da instalação, marcado no Inputar da fixa (fn_pedido_inputado.p_agenda). */
  agenda?: string;
}

interface ExecutarAcaoResult {
  success: boolean;
  proximoBko?: { id: string; nome: string } | null;
  proximaColuna: string;
}

// Colunas válidas para retorno quando recuperado
const COLUNAS_VALIDAS_RETORNO = [
  'CRIAÇÃO PEDIDO',
  'ACEITE',
  'INPUT',
  'INPUT REALIZADO MOVEL',
  'EM INSTALAÇÃO',
  'QUALIDADE'
];

// Interface para dados do webhook
interface DadosWebhook {
  inbox_id: number;
  consultor_id: string;
  telefone: string;
  gestor_nome: string;
  razao_social: string;
  cnpj: string;
  produto_nome: string;
  plano_nome: string;
  quantidade: number;
  subtotal: string;
  // Campos para FIBRA
  familia: string;
  data_agendamento: string | null;
  agenda_tipo: string | null;
  // Campos para PORTABILIDADE
  linha: string | null;
  ddd: string | null;
}

// Função para buscar dados necessários para o webhook
async function buscarDadosParaWebhook(pedidoId: string, itemId: string): Promise<DadosWebhook | null> {
  try {
    // Buscar pedido
    const { data: pedido, error: pedidoError } = await supabase
      .from('pedidos')
      .select('id, cliente_id, consultor_id')
      .eq('id', pedidoId)
      .single();

    if (pedidoError || !pedido) {
      console.warn('⚠️ [Webhook] Erro ao buscar pedido:', pedidoError);
      return null;
    }

    // Buscar cliente
    const { data: cliente, error: clienteError } = await supabase
      .from('clientes')
      .select('razao_social, cnpj')
      .eq('id', pedido.cliente_id)
      .single();

    if (clienteError || !cliente) {
      console.warn('⚠️ [Webhook] Erro ao buscar cliente:', clienteError);
      return null;
    }

    // Buscar inbox_id do consultor
    const { data: consultor, error: consultorError } = await supabase
      .from('usuarios')
      .select('bee_inbox_id')
      .eq('id', pedido.consultor_id)
      .single();

    if (consultorError || !consultor?.bee_inbox_id) {
      console.warn('⚠️ [Webhook] Consultor sem inbox_id:', consultorError);
      return null;
    }

    // Buscar item (inclui gestor_id, data_agendamento, agenda_tipo)
    const { data: item, error: itemError } = await supabase
      .from('itens_pedido')
      .select('quantidade, subtotal, plano_nome, produto_id, gestor_id, data_agendamento, agenda_tipo, ddd, linha')
      .eq('id', itemId)
      .single();

    if (itemError || !item) {
      console.warn('⚠️ [Webhook] Erro ao buscar item:', itemError);
      return null;
    }

    // Buscar contato do GESTOR do item (tabela cliente_contato)
    // Essa é a fonte correta: o gestor associado ao item específico
    let telefone = '';
    let gestor_nome = 'Cliente';

    if (item.gestor_id) {
      const { data: contato, error: contatoError } = await supabase
        .from('cliente_contato')
        .select('nome, telefone, whatsapp')
        .eq('id', item.gestor_id)
        .single();

      if (contatoError) {
        console.warn('⚠️ [Webhook] Erro ao buscar gestor do item:', contatoError);
      } else if (contato) {
        // Prioridade: whatsapp > telefone
        telefone = contato.whatsapp || contato.telefone || '';
        gestor_nome = contato.nome || 'Cliente';
        console.log('📞 [Webhook] Contato encontrado via gestor_id:', {
          gestor_id: item.gestor_id,
          nome: gestor_nome,
          telefone
        });
      }
    }

    // Fallback: se não tem gestor_id, buscar contato principal do cliente
    if (!telefone) {
      console.log('ℹ️ [Webhook] Sem gestor_id, buscando contato principal do cliente');
      const { data: contatoPrincipal } = await supabase
        .from('cliente_contato')
        .select('nome, telefone, whatsapp')
        .eq('cliente_id', pedido.cliente_id)
        .eq('is_principal', true)
        .single();

      if (contatoPrincipal) {
        telefone = contatoPrincipal.whatsapp || contatoPrincipal.telefone || '';
        gestor_nome = contatoPrincipal.nome || 'Cliente';
      }
    }

    if (!telefone) {
      console.warn('⚠️ [Webhook] Nenhum telefone encontrado para envio');
      return null;
    }

    // Buscar produto (inclui familia)
    const { data: produto } = await supabase
      .from('produtos')
      .select('nome, familia')
      .eq('id', item.produto_id)
      .single();

    console.log('📤 [Webhook] Dados coletados:', {
      inbox_consultor: consultor.bee_inbox_id,
      telefone_gestor: telefone,
      nome_gestor: gestor_nome,
      empresa: cliente.razao_social
    });

    return {
      inbox_id: consultor.bee_inbox_id,
      consultor_id: pedido.consultor_id,
      telefone,
      gestor_nome,
      razao_social: cliente.razao_social || 'Não informado',
      cnpj: cliente.cnpj || 'Não informado',
      produto_nome: obterNomeAmigavel(produto?.nome, item.plano_nome),
      plano_nome: item.plano_nome || 'Plano',
      quantidade: item.quantidade || 1,
      subtotal: item.subtotal?.toFixed(2) || '0,00',
      // Campos para FIBRA
      familia: produto?.familia || '',
      data_agendamento: item.data_agendamento || null,
      agenda_tipo: item.agenda_tipo || null,
      // Campos para PORTABILIDADE
      ddd: item.ddd || null,
      linha: item.linha || null
    };
  } catch (error) {
    console.error('❌ [Webhook] Erro ao buscar dados:', error);
    return null;
  }
}

// Data da agenda em São Paulo; instalação leva o TURNO ("29/09/2026, das 15h às 18h").
function formatarDataAgendamento(data: string | null, agendaTipo?: string | null): string {
  if (!data) return 'A definir';
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return data;
  const dia = d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric' });
  if (agendaTipo?.toUpperCase() === 'PORTABILIDADE') return dia;
  const turno = turnoDaAgenda(d);
  return turno ? `${dia}, das ${rotuloTurno(turno)}` : dia;
}

// Função para formatar mensagem substituindo variáveis
// Função para formatar número de linha como (DDD) 9XXXX-XXXX
function formatarLinhaTelefone(ddd: string | null, linha: string | null): string {
  if (!linha) return 'A definir';
  const linhaLimpa = linha.replace(/\D/g, '');
  if (!linhaLimpa) return 'A definir';
  const dddFormatado = ddd ? `(${ddd}) ` : '';
  if (linhaLimpa.length >= 9) {
    return `${dddFormatado}${linhaLimpa.slice(0, 5)}-${linhaLimpa.slice(5)}`;
  }
  return `${dddFormatado}${linhaLimpa}`;
}

function formatarMensagem(template: string, dados: DadosWebhook): string {
  // Data de portabilidade: usa data_agendamento quando agenda_tipo é PORTABILIDADE
  const dataPortabilidade = dados.agenda_tipo?.toUpperCase() === 'PORTABILIDADE'
    ? formatarDataAgendamento(dados.data_agendamento, 'PORTABILIDADE')
    : 'A definir';

  return template
    .replace(/\{\{gestor_nome\}\}/g, dados.gestor_nome)
    .replace(/\{\{razao_social\}\}/g, dados.razao_social)
    .replace(/\{\{cnpj\}\}/g, dados.cnpj)
    .replace(/\{\{produto_nome\}\}/g, dados.produto_nome)
    .replace(/\{\{plano_nome\}\}/g, dados.plano_nome)
    .replace(/\{\{quantidade\}\}/g, dados.quantidade.toString())
    .replace(/\{\{subtotal\}\}/g, dados.subtotal)
    .replace(/\{\{data_agendamento\}\}/g, formatarDataAgendamento(dados.data_agendamento, dados.agenda_tipo))
    .replace(/\{\{linha_numero\}\}/g, formatarLinhaTelefone(dados.ddd, dados.linha))
    .replace(/\{\{data_portabilidade\}\}/g, dataPortabilidade);
}

// Verifica se o produto é da família FIBRA
function isProdutoFibra(familia: string): boolean {
  const familiaUpper = familia?.toUpperCase() || '';
  return familiaUpper.includes('FIBRA') || familiaUpper === 'FIXA BÁSICA';
}

// Função para enviar webhook de mensagem WhatsApp (item único)
async function enviarWebhookBKO(webhookKey: string, pedidoId: string, itemId: string): Promise<void> {
  const dados = await buscarDadosParaWebhook(pedidoId, itemId);
  if (!dados) {
    console.warn('⚠️ [Webhook] Não foi possível obter dados para envio');
    return;
  }

  // Determinar se é produto FIBRA e usar template específico
  const isFibra = isProdutoFibra(dados.familia);
  let templateKey = webhookKey;
  
  if (isFibra && MENSAGENS_WEBHOOK[`${webhookKey}_FIBRA`]) {
    templateKey = `${webhookKey}_FIBRA`;
    console.log('📡 [Webhook] Produto FIBRA detectado, usando template:', templateKey);
  }

  const configMensagem = MENSAGENS_WEBHOOK[templateKey];
  if (!configMensagem) {
    console.log('ℹ️ [Webhook] Nenhuma mensagem configurada para:', templateKey);
    return;
  }

  // Obter mensagem rotacionada
  const templateMensagem = await obterMensagemRotacionada(templateKey);
  const mensagemFormatada = formatarMensagem(templateMensagem, dados);

  const payload = {
    inbox_id: dados.inbox_id,
    consultor_id: dados.consultor_id,
    telefone: dados.telefone,
    nome: dados.gestor_nome,
    razao_social: dados.razao_social,
    cnpj: dados.cnpj,
    mensagem: mensagemFormatada,
    mensagem_template: mensagemFormatada,
    mensagem_privada: configMensagem.mensagem_privada
  };

  console.log('📤 [Webhook] Enviando mensagem BKO:', {
    webhookKey,
    telefone: dados.telefone,
    mensagem_privada: configMensagem.mensagem_privada
  });

  try {
    const { error } = await supabase.functions.invoke('whatsapp-send-tabulacao', {
      body: payload
    });

    if (error) {
      console.error('❌ [Webhook] Erro ao enviar:', error);
    } else {
      console.log('✅ [Webhook] Mensagem enviada com sucesso');
    }
  } catch (err) {
    console.error('❌ [Webhook] Erro ao chamar função:', err);
  }
}

// Função para buscar dados de múltiplos itens para webhook consolidado
async function buscarDadosConsolidadosParaWebhook(pedidoId: string, itensIds: string[]): Promise<{
  dados: DadosWebhook;
  listaItens: string;
  totalGeral: string;
} | null> {
  try {
    // Buscar dados base do primeiro item (gestor, cliente, etc)
    const dadosBase = await buscarDadosParaWebhook(pedidoId, itensIds[0]);
    if (!dadosBase) return null;

    // Buscar todos os itens
    const { data: itens, error } = await supabase
      .from('itens_pedido')
      .select('id, quantidade, subtotal, plano_nome, produto_id, ddd, linha, data_agendamento, agenda_tipo')
      .in('id', itensIds);

    if (error || !itens || itens.length === 0) {
      console.warn('⚠️ [Webhook Consolidado] Erro ao buscar itens:', error);
      return null;
    }

    // Buscar nomes dos produtos
    const produtoIds = [...new Set(itens.map(i => i.produto_id).filter(Boolean))];
    const { data: produtos } = await supabase
      .from('produtos')
      .select('id, nome')
      .in('id', produtoIds);

    const produtosMap = new Map(produtos?.map(p => [p.id, p.nome]) || []);

    // Formatar lista de itens
    const listaFormatada = itens.map(item => {
      const produtoTecnico = produtosMap.get(item.produto_id);
      const nomeExibicao = obterNomeAmigavel(produtoTecnico, item.plano_nome);
      const qtd = item.quantidade || 1;
      const valor = (item.subtotal || 0).toFixed(2);
      let linhaInfo = `• ${nomeExibicao} - ${qtd}x - R$ ${valor}`;
      // Se o item tem linha (portabilidade), adicionar na lista
      if (item.linha) {
        const linhaFormatada = formatarLinhaTelefone(item.ddd, item.linha);
        linhaInfo += ` | Linha: ${linhaFormatada}`;
      }
      return linhaInfo;
    }).join('\n');

    // Calcular total
    const totalGeral = itens.reduce((sum, item) => sum + (item.subtotal || 0), 0);

    return {
      dados: dadosBase,
      listaItens: listaFormatada,
      totalGeral: totalGeral.toFixed(2)
    };
  } catch (error) {
    console.error('❌ [Webhook Consolidado] Erro ao buscar dados:', error);
    return null;
  }
}

// Função para enviar webhook consolidado (múltiplos itens, 1 mensagem)
async function enviarWebhookConsolidado(webhookKey: string, pedidoId: string, itensIds: string[]): Promise<void> {
  // Se só tem 1 item, usar o envio normal
  if (itensIds.length === 1) {
    return enviarWebhookBKO(webhookKey, pedidoId, itensIds[0]);
  }

  const dadosConsolidados = await buscarDadosConsolidadosParaWebhook(pedidoId, itensIds);
  if (!dadosConsolidados) {
    console.warn('⚠️ [Webhook Consolidado] Não foi possível obter dados para envio');
    return;
  }

  const { dados, listaItens, totalGeral } = dadosConsolidados;

  // Determinar se é produto FIBRA e usar template específico
  const isFibra = isProdutoFibra(dados.familia);
  let baseKey = webhookKey;
  
  if (isFibra && MENSAGENS_WEBHOOK[`${webhookKey}_FIBRA_CONSOLIDADO`]) {
    baseKey = `${webhookKey}_FIBRA`;
    console.log('📡 [Webhook Consolidado] Produto FIBRA detectado, usando template:', baseKey);
  }

  // Usar template consolidado
  const consolidadoKey = `${baseKey}_CONSOLIDADO`;
  const configMensagem = MENSAGENS_WEBHOOK[consolidadoKey] || MENSAGENS_WEBHOOK[baseKey];
  if (!configMensagem) {
    console.log('ℹ️ [Webhook Consolidado] Nenhuma mensagem configurada para:', consolidadoKey);
    return;
  }

  // Obter mensagem rotacionada e formatar com lista de itens
  const templateMensagem = await obterMensagemRotacionada(consolidadoKey);
  let mensagemFormatada = formatarMensagem(templateMensagem, dados);
  mensagemFormatada = mensagemFormatada
    .replace(/\{\{lista_itens\}\}/g, listaItens)
    .replace(/\{\{total_geral\}\}/g, totalGeral);

  const payload = {
    inbox_id: dados.inbox_id,
    consultor_id: dados.consultor_id,
    telefone: dados.telefone,
    nome: dados.gestor_nome,
    razao_social: dados.razao_social,
    cnpj: dados.cnpj,
    mensagem: mensagemFormatada,
    mensagem_template: mensagemFormatada,
    mensagem_privada: `${configMensagem.mensagem_privada} (${itensIds.length} itens)`
  };

  console.log('📤 [Webhook Consolidado] Enviando mensagem:', {
    webhookKey: consolidadoKey,
    isFibra,
    telefone: dados.telefone,
    totalItens: itensIds.length,
    mensagem_privada: payload.mensagem_privada
  });

  try {
    const { error } = await supabase.functions.invoke('whatsapp-send-tabulacao', {
      body: payload
    });

    if (error) {
      console.error('❌ [Webhook Consolidado] Erro ao enviar:', error);
    } else {
      console.log('✅ [Webhook Consolidado] Mensagem enviada com sucesso');
    }
  } catch (err) {
    console.error('❌ [Webhook Consolidado] Erro ao chamar função:', err);
  }
}

export function useBKOAcao() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { invalidateKanban } = useInvalidateAllPedidoQueries();
  const { moveCard, moverPedidoJunto } = usePedidoMovement();

  const mutation = useMutation({
    mutationFn: async ({ itemId, pedidoId, colunaAtual, colunaOriginal }: ExecutarAcaoParams): Promise<ExecutarAcaoResult> => {
      if (colunaAtual.toUpperCase() === 'CRIAÇÃO PEDIDO') {
        const cpf = await concluirCriacaoSeCpf(pedidoId, [itemId]);
        if (cpf) return cpf;
      }
      const config = ACOES_CONFIG[colunaAtual.toUpperCase()];
      
      if (!config) {
        throw new Error(`Nenhuma ação disponível para status: ${colunaAtual}`);
      }

      // O input é um botão só, o Inputar da triagem (fn_pedido_inputado): a fixa pede ali a
      // data e o turno da instalação e vai direto para a Em instalação (Douglas 30/10/2026).
      if (colunaAtual.toUpperCase() === 'INPUT') {
        throw new Error('🔒 Pedidos em INPUT só se movem pela triagem da fila (clique no cabeçalho da coluna no Kanban).');
      }

      // ✅ Para PEDIDO RECUPERADO, volta pro EXATO lugar de onde foi cancelado (snapshot).
      // Sem lista branca: cancelou de SMS ENVIADO (PRE_ATIVO), volta pra SMS ENVIADO.
      let proximaColuna = config.proximaColuna;
      let rpcAtribuicao = config.rpcAtribuicao;
      let quadroSnapshotRet: string | null = null; // quadro exato do snapshot (retomada)

      if (colunaAtual.toUpperCase() === 'PEDIDO RECUPERADO') {
        // Mesma régua do badge/triagem: snapshot válido mais recente (ignora
        // estados poluídos PEDIDO RECUPERADO/CANCELADO), fallback CRIAÇÃO PEDIDO.
        const destino = await resolverDestinoRetomada(itemId);
        proximaColuna = destino.coluna;
        quadroSnapshotRet = destino.quadro;
        console.log('🔄 [useBKOAcao] PEDIDO RECUPERADO - destino:', destino.quadro, destino.coluna);

        // Definir RPC de atribuição baseado na coluna de destino
        const rpcPorColuna: Record<string, 'aceite' | 'input' | 'tramitacao' | undefined> = {
          'ACEITE': undefined,
          'INPUT': 'input',
          'INPUT REALIZADO MOVEL': 'tramitacao',
          'EM INSTALAÇÃO': 'tramitacao',
          'QUALIDADE': undefined,
          'CRIAÇÃO PEDIDO': undefined
        };
        rpcAtribuicao = rpcPorColuna[proximaColuna];
      }

      // Determinar quadro de destino (pode mudar de quadro!)
      // Para INPUT REALIZADO, estamos no TRAMITACAO_VIVO
      const quadroAtualNormalizado = colunaAtual.toUpperCase();
      let quadroDestino: QuadroEnum;

      if (quadroSnapshotRet) {
        quadroDestino = quadroSnapshotRet as QuadroEnum; // retomada: quadro EXATO do snapshot
      } else if (config.proximoQuadro) {
        quadroDestino = config.proximoQuadro as QuadroEnum;
      } else if (quadroAtualNormalizado === 'INPUT REALIZADO MOVEL') {
        quadroDestino = 'QUALIDADE';
      } else {
        quadroDestino = 'CONTRATO_PRIME';
      }

      console.log('🔄 [useBKOAcao] Executando ação:', {
        colunaAtual,
        proximaColuna,
        quadroDestino,
        rpcAtribuicao
      });

      // 1. Buscar status_id da próxima coluna NO QUADRO CORRETO
      const { data: statusData, error: statusError } = await supabase
        .from('status_catalogo')
        .select('id')
        .eq('quadro', quadroDestino)
        .eq('coluna', proximaColuna)
        .eq('ativo', true)
        .limit(1)
        .maybeSingle();

      if (statusError) {
        console.error('❌ Erro ao buscar status:', statusError);
        throw statusError;
      }

      if (!statusData?.id) {
        throw new Error(`Status "${proximaColuna}" não encontrado no quadro "${quadroDestino}"`);
      }

      // 2. Mover o item para a próxima coluna (e possivelmente novo quadro)
      await moveCard({
        pedidoId,
        itemId,
        toQuadro: quadroDestino,
        toColuna: proximaColuna,
        toStatusId: statusData.id,
        motivo: `Ação BKO: ${config.label} → ${quadroDestino}/${proximaColuna}`,
        // Retomada de snapshot: volta pro estado exato, sem passar pelo grafo de transição.
        skipValidacao: !!quadroSnapshotRet,
      });

      // 3. Atribuir próximo BKO se necessário
      let proximoBko: { id: string; nome: string } | null = null;
      
      if (rpcAtribuicao) {
        const rpcName = `atribuir_bko_${rpcAtribuicao}`;
        console.log(`📞 [useBKOAcao] Chamando RPC: ${rpcName}`);
        
        const { data: bkoData, error: bkoError } = await supabase
          .rpc(rpcName as any, { p_pedido_id: pedidoId })
          .single();

        if (bkoError) {
          console.warn(`⚠️ Erro ao atribuir BKO (${rpcName}):`, bkoError);
          // Não bloquear a ação, apenas logar o erro
        } else if (bkoData) {
          proximoBko = {
            id: (bkoData as any).bko_id || '',
            nome: (bkoData as any).bko_nome || 'Não atribuído'
          };
          console.log('✅ [useBKOAcao] BKO atribuído:', proximoBko);
          
          // ✅ ENVIAR NOTIFICAÇÃO PARA O NOVO BKO
          if (proximoBko.id) {
            // Buscar número do pedido para a notificação
            const { data: pedidoInfo } = await supabase
              .from('pedidos')
              .select('numero_pedido')
              .eq('id', pedidoId)
              .single();
            
            const numeroPedido = pedidoInfo?.numero_pedido || pedidoId.slice(0, 8);
            
            // Títulos e mensagens por tipo de BKO
            const notificacaoConfig: Record<string, { tipo: string; titulo: string; mensagem: string }> = {
              'aceite': {
                tipo: 'novo_pedido_aceite',
                titulo: '📝 Novo Pedido para Aceite Digital!',
                mensagem: `Pedido #${numeroPedido} precisa da assinatura digital do cliente. Por favor, acompanhe o processo.`
              },
              'input': {
                tipo: 'novo_pedido_input',
                titulo: '📤 Novo Pedido para Input!',
                mensagem: `Pedido #${numeroPedido} está pronto para input no sistema da operadora.`
              },
              'tramitacao': {
                tipo: 'novo_pedido_tramitacao',
                titulo: '⚡ Novo Pedido para Tramitação!',
                mensagem: `Pedido #${numeroPedido} foi inputado e aguarda ativação. Por favor, acompanhe a tramitação.`
              }
            };
            
            const notifConfig = notificacaoConfig[rpcAtribuicao!];
            if (notifConfig) {
              const { error: notifError } = await supabase
                .from('notificacoes')
                .insert({
                  usuario_id: proximoBko.id,
                  tipo: notifConfig.tipo,
                  titulo: notifConfig.titulo,
                  mensagem: notifConfig.mensagem,
                  pedido_id: pedidoId,
                  prioridade: 'alta'
                });
              
              if (notifError) {
                console.warn('⚠️ [useBKOAcao] Erro ao enviar notificação (não crítico):', notifError);
              } else {
                console.log('🔔 [useBKOAcao] Notificação enviada para BKO:', proximoBko.nome);
              }
            }
          }
        }
      }

      // 4. Registrar no histórico de movimentos (movimento table)
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Buscar número do pedido para registro
        const { data: pedidoData } = await supabase
          .from('pedidos')
          .select('numero_pedido')
          .eq('id', pedidoId)
          .single();

        await supabase.from('movimento').insert({
          entidade: 'pedido',
          entidade_id: itemId,
          tipo: 'acao_bko',
          de: colunaAtual,
          para: proximaColuna,
          motivo: `${config.icone} ${config.label} → ${proximaColuna}`,
          usuario_id: user?.id,
          icone: config.icone,
          cor: config.corClasse.replace('bg-', '').split(' ')[0]
        });
      } catch (movError) {
        console.warn('⚠️ Erro ao registrar movimento (não crítico):', movError);
      }

      // 5. Enviar webhook WhatsApp (não bloqueante)
      if (config.webhookKey) {
        enviarWebhookBKO(config.webhookKey, pedidoId, itemId).catch(err => {
          console.warn('⚠️ Erro ao enviar webhook (não crítico):', err);
        });
      }

      return {
        success: true,
        proximoBko,
        proximaColuna,
      };
    },
    onSuccess: (result, variables) => {
      const config = ACOES_CONFIG[variables.colunaAtual.toUpperCase()];
      
      // Toast de sucesso
      if (result.proximoBko?.nome) {
        toast({
          title: `${config?.icone || '✅'} ${config?.label || 'Ação concluída'}!`,
          description: `Próximo responsável: ${result.proximoBko.nome}`,
        });
      } else {
        toast({
          title: `${config?.icone || '✅'} ${config?.label || 'Ação concluída'}!`,
          description: `Item movido para ${result.proximaColuna}`,
        });
      }

      // Invalidar queries do kanban (cirúrgico — não usar invalidateAll aqui)
      invalidateKanban();
      queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', variables.pedidoId] });
      queryClient.invalidateQueries({ queryKey: ['pedido-detail', variables.pedidoId] });
    },
    onError: (error: Error, variables) => {
      console.error('❌ [useBKOAcao] Erro:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao executar ação',
        description: error.message || 'Não foi possível processar a ação'
      });
    }
  });

  // Mutação para ação consolidada (múltiplos itens)
  const mutationConsolidada = useMutation({
    mutationFn: async ({ itensIds: itensPedidos, pedidoId, colunaAtual, origemTriagem, agenda }: ExecutarAcaoConsolidadaParams): Promise<{
      success: boolean;
      processados: number;
      proximaColuna: string;
    }> => {
      let itensIds = itensPedidos;
      if (colunaAtual.toUpperCase() === 'CRIAÇÃO PEDIDO') {
        const cpf = await concluirCriacaoSeCpf(pedidoId, itensIds);
        if (cpf) return cpf;
      }
      // O Inputar da Triagem Aguardando HP é o botão do Input (Douglas 27/10/2026): o HP
      // que liberou é inputado ali mesmo, sem voltar para a fila do Input.
      const inputar = colunaAtual.toUpperCase() === 'INPUT' || colunaAtual.toUpperCase() === 'AGUARDANDO HP';
      const config = ACOES_CONFIG[inputar ? 'INPUT' : colunaAtual.toUpperCase()];
      
      if (!config) {
        throw new Error(`Nenhuma ação disponível para status: ${colunaAtual}`);
      }

      // O PEDIDO INPUTADO MORA NO BANCO (Douglas 24/10/2026): a Triagem Input e a resposta da
      // Konnect no grupo apertam o mesmo botão, fn_pedido_inputado — a data e o turno da
      // instalação marcados no Inputar (30/10/2026: quem inputa marca a data oficial, e a TV,
      // o Mesh e o ponto adicional levam a mesma), o pedido inteiro (fixa → Em instalação,
      // móvel → Input realizado Móvel), a timeline e o BKO da tramitação, numa transação.
      if (inputar) {
        if (!origemTriagem) {
          throw new Error(`🔒 Pedidos em ${colunaAtual.toUpperCase()} só se movem pela triagem da fila (clique no cabeçalho da coluna no Kanban).`);
        }
        if (await itensTemFixa(itensIds)) await exigirInputFixaAprovado(pedidoId);
        const pedidoInputado = supabase.rpc.bind(supabase) as unknown as (fn: string, args: Record<string, unknown>) => Promise<{
          data: { movidos?: Array<{ item_id: string }> } | null;
          error: { message?: string } | null;
        }>;
        const { data, error } = await pedidoInputado('fn_pedido_inputado', { p_pedido_id: pedidoId, p_itens: itensIds, ...(agenda ? { p_agenda: agenda } : {}) });
        if (error) throw new Error(error.message || 'O pedido não andou.');
        const movidos = (data?.movidos ?? []).map((m) => m.item_id);
        marcarTelasDeForaDesatualizadas(queryClient);
        queryClient.invalidateQueries({ queryKey: ['pedido', pedidoId] });
        queryClient.invalidateQueries({ queryKey: ['timeline-pedido', pedidoId] });
        queryClient.invalidateQueries({ queryKey: ['pedidos-cliente-visao360'] });
        if (config.webhookKey) {
          enviarWebhookConsolidado(config.webhookKey, pedidoId, movidos).catch(err => {
            console.warn('⚠️ Erro ao enviar webhook consolidado (não crítico):', err);
          });
        }
        return { success: true, processados: movidos.length, proximaColuna: config.proximaColuna };
      }

      const proximaColuna = config.proximaColuna;
      let quadroDestino: QuadroEnum = config.proximoQuadro as QuadroEnum || 'CONTRATO_PRIME';
      
      if (colunaAtual.toUpperCase() === 'INPUT REALIZADO MOVEL') {
        quadroDestino = 'QUALIDADE';
      } else if (colunaAtual.toUpperCase() === 'INPUT') {
        quadroDestino = 'TRAMITACAO_VIVO';
      }

      console.log('🔄 [useBKOAcao] Executando ação CONSOLIDADA:', {
        colunaAtual,
        proximaColuna,
        quadroDestino,
        totalItens: itensIds.length
      });

      // O PEDIDO ANDA INTEIRO (Douglas 10/10/2026): uma chamada move todas as
      // linhas na mesma transação, ou nenhuma. No Contrato leva também as linhas
      // do pedido que ficaram para trás na esteira. Antes era um moveCard por
      // linha, e uma interrupção no meio deixava o pedido picado.
      const quadroOrigem = colunaAtual.toUpperCase().startsWith('INPUT REALIZADO') ? 'TRAMITACAO_VIVO' : 'CONTRATO_PRIME';
      const noContrato = quadroOrigem === 'CONTRATO_PRIME' && colunaAtual.toUpperCase() !== 'PEDIDO RECUPERADO';
      const movimento = await moverPedidoJunto({
        pedidoId,
        itensIds,
        quadroOrigem,
        colunaOrigem: colunaAtual,
        toQuadro: quadroDestino,
        toColuna: proximaColuna,
        motivo: `Ação BKO Consolidada: ${config.label} → ${quadroDestino}/${proximaColuna}`,
        origemTriagem: origemTriagem ?? false,
        arrastar: noContrato,
      });
      const usuarioId = await meuUsuarioId();
      itensIds = movimento.movidos.map((m) => m.item_id);

      // As colunas sao as da tabela `movimento`: campo/de/para/motivo/usuario_id.
      const movimentosBatch = movimento.movidos.map((m) => ({
        entidade: 'pedido',
        entidade_id: m.item_id,
        tipo: 'acao_bko_consolidada',
        campo: 'coluna_atual',
        de: m.coluna_anterior,
        para: m.coluna,
        motivo: `${config.icone} ${config.label} (consolidado)`,
        usuario_id: usuarioId,
        icone: config.icone,
        cor: config.corClasse.replace('bg-', '').split(' ')[0],
      }));
      /* O registro e a atribuição do BKO saem juntos, sem um esperar o outro
         (a triagem clica e faz, Douglas 29/10/2026). */
      const registrarMovimentos = async () => {
        if (movimentosBatch.length === 0) return;
        const { error: movError } = await supabase.from('movimento').insert(movimentosBatch);
        if (movError) {
          console.warn('⚠️ Erro ao registrar movimentos (não crítico):', movError);
        }
      };

      // 3. Atribuir BKO (apenas uma vez, para o primeiro item) + ENVIAR NOTIFICAÇÃO
      const atribuirBko = async () => {
        if (config.rpcAtribuicao) {
          const rpcName = `atribuir_bko_${config.rpcAtribuicao}`;
          try {
            const { data: bkoData } = await supabase.rpc(rpcName as any, { p_pedido_id: pedidoId }).single();
          
            // ✅ ENVIAR NOTIFICAÇÃO PARA O NOVO BKO
            const bkoId = (bkoData as any)?.bko_id;
            const bkoNome = (bkoData as any)?.bko_nome;
          
            if (bkoId) {
              const { data: pedidoInfo } = await supabase
                .from('pedidos')
                .select('numero_pedido')
                .eq('id', pedidoId)
                .single();
            
              const numeroPedido = pedidoInfo?.numero_pedido || pedidoId.slice(0, 8);
            
              const notificacaoConfig: Record<string, { tipo: string; titulo: string; mensagem: string }> = {
                'aceite': {
                  tipo: 'novo_pedido_aceite',
                  titulo: '📝 Novo Pedido para Aceite Digital!',
                  mensagem: `Pedido #${numeroPedido} (${itensIds.length} itens) precisa da assinatura digital do cliente.`
                },
                'input': {
                  tipo: 'novo_pedido_input',
                  titulo: '📤 Novo Pedido para Input!',
                  mensagem: `Pedido #${numeroPedido} (${itensIds.length} itens) está pronto para input no sistema.`
                },
                'tramitacao': {
                  tipo: 'novo_pedido_tramitacao',
                  titulo: '⚡ Novo Pedido para Tramitação!',
                  mensagem: `Pedido #${numeroPedido} (${itensIds.length} itens) foi inputado e aguarda ativação.`
                }
              };
            
              const notifConfig = notificacaoConfig[config.rpcAtribuicao];
              if (notifConfig) {
                await supabase.from('notificacoes').insert({
                  usuario_id: bkoId,
                  tipo: notifConfig.tipo,
                  titulo: notifConfig.titulo,
                  mensagem: notifConfig.mensagem,
                  pedido_id: pedidoId,
                  prioridade: 'alta'
                });
                console.log('🔔 [useBKOAcao Consolidada] Notificação enviada para BKO:', bkoNome);
              }
            }
          } catch (bkoError) {
            console.warn('⚠️ Erro ao atribuir BKO (não crítico):', bkoError);
          }
        }
      };
      await Promise.all([registrarMovimentos(), atribuirBko()]);

      // 4. Enviar UMA ÚNICA mensagem consolidada (não bloqueante)
      if (config.webhookKey) {
        enviarWebhookConsolidado(config.webhookKey, pedidoId, itensIds).catch(err => {
          console.warn('⚠️ Erro ao enviar webhook consolidado (não crítico):', err);
        });
      }

      return {
        success: true,
        processados: itensIds.length,
        proximaColuna
      };
    },
    onSuccess: (result, variables) => {
      const config = ACOES_CONFIG[variables.colunaAtual.toUpperCase()];
      
      toast({
        title: `${config?.icone || '✅'} ${result.processados} itens processados!`,
        description: `Movidos para ${result.proximaColuna}. Enviada 1 mensagem consolidada.`,
      });

      // Invalidar queries do kanban (cirúrgico — não usar invalidateAll aqui).
      // Da triagem, o Kanban fica marcado e se lê quando aparece.
      if (variables.origemTriagem) marcarTelasDeForaDesatualizadas(queryClient);
      else invalidateKanban();
      queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', variables.pedidoId] });
      queryClient.invalidateQueries({ queryKey: ['pedido-detail', variables.pedidoId] });
    },
    onError: (error: Error) => {
      console.error('❌ [useBKOAcao Consolidada] Erro:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao executar ação consolidada',
        description: error.message || 'Não foi possível processar os itens'
      });
    }
  });

  // Função helper para verificar se existe ação para determinada coluna
  const temAcaoDisponivel = (colunaAtual: string): boolean => {
    return !!ACOES_CONFIG[colunaAtual?.toUpperCase()];
  };

  // Função helper para obter config da ação
  const getAcaoConfig = (colunaAtual: string): AcaoConfig | null => {
    return ACOES_CONFIG[colunaAtual?.toUpperCase()] || null;
  };

  return {
    executarAcao: mutation.mutate,
    executarAcaoAsync: mutation.mutateAsync,
    executarAcaoConsolidada: mutationConsolidada.mutate,
    executarAcaoConsolidadaAsync: mutationConsolidada.mutateAsync,
    isExecutando: mutation.isPending,
    isExecutandoConsolidada: mutationConsolidada.isPending,
    temAcaoDisponivel,
    getAcaoConfig,
    ACOES_CONFIG
  };
}
